import Permission from "../../../Modules/Permission/models/Permission.model";
import { Service } from "typedi";
import { HttpException } from "../../../utils/exceptions/httpException";
import { TFunction } from "i18next";
import { status } from "../../../utils/helpers/api.responses";
import Role from "../../../Modules/Role/models/role.model";

@Service()
export class PermissionService {

    // Create a new permission
    public async createPermission(name: string, t: TFunction, description?: string) {
        try {
            const slug = name.trim().toLowerCase().replace(/\s+/g, '-');
            const existing = await Permission.find({ slug })
            if (existing) {
                throw new HttpException(status.ResourceExist, t('General.already_exist', { field: t('Role.permissions') }))
            }
            const permission = new Permission({ name, description, slug });
            await permission.save();
            return permission;
        } catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorCreating', { field: t('Role.permissions') }))

        }
    }

    // Get permission by ID
    public async getPermissionById(id: string, t: TFunction) {
        try {
            const permission = await Permission.findById(id);
            if (!permission) {
                throw new HttpException(status.NotFound, t("General.not_found", { field: t("Role.permissions") }));
            }
            return permission;
        }
        catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Role.permissions') }))

        }
    }

    // Get all permissions with filters, pagination, and sorting
    public async getAllPermissions(filters: any, page: number, limit: number, sort: any, t: TFunction) {
        try {
            const skip = (page - 1) * limit;

            const [result, totalCount] = await Promise.all([
                Permission.find(filters).sort(sort).skip(skip).limit(limit),
                Permission.countDocuments(filters)
            ]);

            return {
                data: result,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit)
            };
        } catch (error) {

            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('Role.permissions') }))

        }
    }

    // Update an existing permission
    public async updatePermission(id: string, updates: any, t: TFunction) {
        try {

            if (updates.name) {
                const newSlug = updates.name.trim().toLowerCase().replace(/\s+/g, '-');

                const existing = await Permission.findOne({ slug: newSlug });
                if (existing && existing._id.toString() !== id) {
                    throw new HttpException(
                        status.ResourceExist,
                        t('General.already_exist', { field: t('Role.permissions') })
                    );
                }

                updates.slug = newSlug;
            }

            const permission = await Permission.findByIdAndUpdate(id, updates, { new: true });
            if (!permission) {
                throw new HttpException(status.NotFound, t("General.not_found", { field: t("Role.permissions") }));
            }
            return permission;
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Role.permissions') }))

        }
    }

    // Delete a permission by ID
    public async deletePermission(id: string, t: TFunction) {
        try {

            const isUsed = await Role.findOne({ permissions: id });
            if (isUsed) {
                throw new HttpException(
                    status.BadRequest,
                    t('Role.permissionInUse', { role: isUsed.name })
                );
            }


            const permission = await Permission.findByIdAndDelete(id);
            if (!permission) {
                throw new HttpException(status.NotFound, t("General.not_found", { field: t("Role.permissions") }));
            }
            return true;
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorDeleting', { field: t('Role.permissions') }))
        }
    }
}
